export type AuraType =
  | 'CITY_SOUL'
  | 'SOFT_CHAOS'
  | 'GOLDEN_HOUR'
  | 'NEON_WOLF'
  | 'QUIET_STORM'
  | 'SOLAR_BURST';

export type AuraDimension =
  | 'social'
  | 'chill'
  | 'adventure'
  | 'creative'
  | 'nightlife'
  | 'focus'
  | 'romantic'
  | 'spontaneous';

export interface QuestionOption {
  id: string;
  text: string;
  auras: AuraType[];
  scores?: Partial<Record<AuraDimension, number>>;
}

export interface Question {
  id: string;
  text: string;
  hint: string;
  type: 'single' | 'multi';
  maxSelections?: number;
  options: QuestionOption[];
}

export interface AuraMeta {
  type: AuraType;
  name: string;
  icon: string;
  description: string;
  traits: string[];
  color: string;
  glowColor: string;
  gradient: [string, string, string];
  relatedAuras: AuraType[];
}

export interface AuraScoreResult {
  auraType: AuraType;
  scoreBreakdown: Record<AuraDimension, number>;
  dominantDimensions: AuraDimension[];
}

const AURA_DIMENSION_FALLBACK: Record<AuraType, Partial<Record<AuraDimension, number>>> = {
  CITY_SOUL: { adventure: 2, social: 1, creative: 1 },
  SOFT_CHAOS: { spontaneous: 2, social: 1, adventure: 1 },
  GOLDEN_HOUR: { chill: 2, romantic: 1, focus: 1 },
  NEON_WOLF: { nightlife: 2, adventure: 1, spontaneous: 1 },
  QUIET_STORM: { creative: 2, focus: 1, chill: 1 },
  SOLAR_BURST: { social: 2, spontaneous: 1, adventure: 1 },
};

const AURA_PAIR_MAP: Record<string, AuraType> = {
  'adventure:social': 'CITY_SOUL',
  'adventure:spontaneous': 'SOFT_CHAOS',
  'chill:focus': 'QUIET_STORM',
  'chill:romantic': 'GOLDEN_HOUR',
  'creative:focus': 'QUIET_STORM',
  'creative:social': 'CITY_SOUL',
  'adventure:nightlife': 'NEON_WOLF',
  'nightlife:social': 'NEON_WOLF',
  'nightlife:spontaneous': 'NEON_WOLF',
  'romantic:social': 'GOLDEN_HOUR',
  'social:spontaneous': 'SOLAR_BURST',
};

const AURA_SINGLE_MAP: Record<AuraDimension, AuraType> = {
  social: 'SOLAR_BURST',
  chill: 'GOLDEN_HOUR',
  adventure: 'CITY_SOUL',
  creative: 'QUIET_STORM',
  nightlife: 'NEON_WOLF',
  focus: 'QUIET_STORM',
  romantic: 'GOLDEN_HOUR',
  spontaneous: 'SOFT_CHAOS',
};

function createEmptyScoreBreakdown(): Record<AuraDimension, number> {
  return {
    social: 0,
    chill: 0,
    adventure: 0,
    creative: 0,
    nightlife: 0,
    focus: 0,
    romantic: 0,
    spontaneous: 0,
  };
}

function addScores(
  target: Record<AuraDimension, number>,
  scores: Partial<Record<AuraDimension, number>>,
  multiplier = 1,
) {
  (Object.entries(scores) as Array<[AuraDimension, number]>).forEach(([dimension, value]) => {
    target[dimension] += value * multiplier;
  });
}

function getOptionScores(option: QuestionOption): Partial<Record<AuraDimension, number>> {
  if (option.scores && Object.keys(option.scores).length) return option.scores;
  const scores: Partial<Record<AuraDimension, number>> = {};
  option.auras.forEach((aura) => {
    Object.entries(AURA_DIMENSION_FALLBACK[aura]).forEach(([dimension, value]) => {
      scores[dimension as AuraDimension] = (scores[dimension as AuraDimension] ?? 0) + (value ?? 0);
    });
  });
  return scores;
}

export const AURA_META: Record<AuraType, AuraMeta> = {
  CITY_SOUL: {
    type: 'CITY_SOUL',
    name: 'City Soul',
    icon: 'map-pin',
    description: "You're giving curious, cinematic and quietly adventurous energy today.",
    traits: ['City Explorer', 'Soft Social', 'Aesthetic Mood'],
    color: '#26D0CE',
    glowColor: 'rgba(38,208,206,0.55)',
    gradient: ['rgba(38,208,206,0.32)', 'rgba(38,208,206,0.10)', 'rgba(38,208,206,0.02)'],
    relatedAuras: ['QUIET_STORM', 'SOFT_CHAOS'],
  },
  SOFT_CHAOS: {
    type: 'SOFT_CHAOS',
    name: 'Soft Chaos',
    icon: 'zap',
    description: 'Spontaneous plans, social sparks, and beautifully unstructured energy.',
    traits: ['Spontaneous', 'Café Hopper', 'Social Today'],
    color: '#F27D26',
    glowColor: 'rgba(242,125,38,0.55)',
    gradient: ['rgba(242,125,38,0.32)', 'rgba(242,125,38,0.10)', 'rgba(242,125,38,0.02)'],
    relatedAuras: ['CITY_SOUL', 'SOLAR_BURST', 'NEON_WOLF'],
  },
  GOLDEN_HOUR: {
    type: 'GOLDEN_HOUR',
    name: 'Golden Hour',
    icon: 'sun',
    description: 'Warm, unhurried, and glowing with intentional presence today.',
    traits: ['Intentional', 'Warmth Seeker', 'Present Moment'],
    color: '#FFB347',
    glowColor: 'rgba(255,179,71,0.55)',
    gradient: ['rgba(255,179,71,0.32)', 'rgba(255,179,71,0.10)', 'rgba(255,179,71,0.02)'],
    relatedAuras: ['QUIET_STORM', 'SOFT_CHAOS'],
  },
  NEON_WOLF: {
    type: 'NEON_WOLF',
    name: 'Neon Wolf',
    icon: 'moon',
    description: 'Bold, charged, and running on electric independent energy today.',
    traits: ['Independent', 'Electrically Bold', 'Night-Ready'],
    color: '#C084FC',
    glowColor: 'rgba(192,132,252,0.55)',
    gradient: ['rgba(192,132,252,0.32)', 'rgba(192,132,252,0.10)', 'rgba(192,132,252,0.02)'],
    relatedAuras: ['SOFT_CHAOS', 'SOLAR_BURST'],
  },
  QUIET_STORM: {
    type: 'QUIET_STORM',
    name: 'Quiet Storm',
    icon: 'cloud',
    description: 'Deeply present, creatively alive, and quietly powerful today.',
    traits: ['Introspective', 'Creatively Driven', 'Deeply Aware'],
    color: '#818CF8',
    glowColor: 'rgba(129,140,248,0.55)',
    gradient: ['rgba(129,140,248,0.32)', 'rgba(129,140,248,0.10)', 'rgba(129,140,248,0.02)'],
    relatedAuras: ['CITY_SOUL', 'GOLDEN_HOUR'],
  },
  SOLAR_BURST: {
    type: 'SOLAR_BURST',
    name: 'Solar Burst',
    icon: 'sun',
    description: 'Radiant, magnetic, and overflowing with outward energy today.',
    traits: ['High Energy', 'Radiantly Open', 'Magnetically Social'],
    color: '#FCD34D',
    glowColor: 'rgba(252,211,77,0.55)',
    gradient: ['rgba(252,211,77,0.32)', 'rgba(252,211,77,0.10)', 'rgba(252,211,77,0.02)'],
    relatedAuras: ['NEON_WOLF', 'SOFT_CHAOS'],
  },
};

export const DAY_VIBES_QUESTIONS: Question[] = [
  {
    id: 'q1',
    text: 'How much energy have you got today?',
    hint: 'Honest answer, not the hopeful one.',
    type: 'single',
    options: [
      { id: 'q1_a', text: 'Full battery. Let’s go.', auras: ['SOLAR_BURST', 'NEON_WOLF'] },
      { id: 'q1_b', text: 'Halfway, warming up', auras: ['SOFT_CHAOS', 'CITY_SOUL'] },
      { id: 'q1_c', text: 'Nearly empty, but I’ll show up', auras: ['SOFT_CHAOS', 'GOLDEN_HOUR'] },
      { id: 'q1_d', text: '1%. Do not disturb.', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
    ],
  },
  {
    id: 'q2',
    text: 'You can teleport for 20 minutes. Where to?',
    hint: 'No traffic, no excuses.',
    type: 'single',
    options: [
      { id: 'q2_a', text: 'A rooftop above the city lights', auras: ['NEON_WOLF', 'CITY_SOUL'] },
      { id: 'q2_b', text: 'A café, headphones on, no small talk', auras: ['QUIET_STORM', 'CITY_SOUL'] },
      { id: 'q2_c', text: 'Just wandering, no destination', auras: ['CITY_SOUL', 'SOFT_CHAOS'] },
      { id: 'q2_d', text: 'My sofa. Obviously.', auras: ['GOLDEN_HOUR', 'QUIET_STORM'] },
    ],
  },
  {
    id: 'q3',
    text: 'A friend texts: “Up for something random?” You reply…',
    hint: 'Go with your gut.',
    type: 'single',
    options: [
      { id: 'q3_a', text: 'Already putting my shoes on', auras: ['SOFT_CHAOS', 'SOLAR_BURST'] },
      { id: 'q3_b', text: 'Define “random” first', auras: ['QUIET_STORM', 'CITY_SOUL'] },
      { id: 'q3_c', text: 'I’ve got a plan, join me instead', auras: ['GOLDEN_HOUR', 'QUIET_STORM'] },
      { id: 'q3_d', text: 'Ask me tomorrow, I’m recharging', auras: ['GOLDEN_HOUR', 'SOFT_CHAOS'] },
    ],
  },
  {
    id: 'q4',
    text: 'Group hangout. Where do you end up?',
    hint: 'Where you’d really sit.',
    type: 'single',
    options: [
      { id: 'q4_a', text: 'The quiet corner, watching everyone', auras: ['QUIET_STORM', 'CITY_SOUL'] },
      { id: 'q4_b', text: 'Deep in a one-on-one chat', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
      { id: 'q4_c', text: 'At the loudest, funniest table', auras: ['GOLDEN_HOUR', 'SOFT_CHAOS'] },
      { id: 'q4_d', text: 'Drifting between every table', auras: ['SOLAR_BURST', 'NEON_WOLF'] },
    ],
  },
  {
    id: 'q5',
    text: 'What’s playing in your head right now?',
    hint: 'Pick up to 2.',
    type: 'multi',
    maxSelections: 2,
    options: [
      { id: 'q5_a', text: 'Something slow and a bit dramatic', auras: ['QUIET_STORM', 'CITY_SOUL'] },
      { id: 'q5_b', text: 'Deep bass that keeps going', auras: ['NEON_WOLF', 'CITY_SOUL'] },
      { id: 'q5_c', text: 'Kitchen-dance pop', auras: ['SOLAR_BURST', 'SOFT_CHAOS'] },
      { id: 'q5_d', text: 'Nothing. Blissful silence.', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
    ],
  },
  {
    id: 'q6',
    text: 'Three free hours today. What really happens?',
    hint: 'The real answer, not the ideal one.',
    type: 'single',
    options: [
      { id: 'q6_a', text: 'I walk somewhere new', auras: ['CITY_SOUL', 'NEON_WOLF'] },
      { id: 'q6_b', text: 'I disappear into a creative project', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
      { id: 'q6_c', text: 'Good food, good people, no plan', auras: ['SOFT_CHAOS', 'GOLDEN_HOUR'] },
      { id: 'q6_d', text: 'A film or book that swallows the day', auras: ['QUIET_STORM', 'CITY_SOUL'] },
    ],
  },
  {
    id: 'q7',
    text: 'Someone waves at you across the room. You…',
    hint: 'What actually happens next.',
    type: 'single',
    options: [
      { id: 'q7_a', text: 'Pretend I didn’t see', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
      { id: 'q7_b', text: 'Wave back and hope that’s enough', auras: ['CITY_SOUL', 'QUIET_STORM'] },
      { id: 'q7_c', text: 'Already walking over', auras: ['SOLAR_BURST', 'SOFT_CHAOS'] },
      { id: 'q7_d', text: 'Depends who it is', auras: ['NEON_WOLF', 'CITY_SOUL'] },
    ],
  },
  {
    id: 'q8',
    text: 'If today were a photo, what would it look like?',
    hint: 'Pick up to 2.',
    type: 'multi',
    maxSelections: 2,
    options: [
      { id: 'q8_a', text: 'Dark and cinematic', auras: ['NEON_WOLF', 'QUIET_STORM'] },
      { id: 'q8_b', text: 'Warm golden light', auras: ['GOLDEN_HOUR', 'SOFT_CHAOS'] },
      { id: 'q8_c', text: 'Neon and electric', auras: ['NEON_WOLF', 'SOLAR_BURST'] },
      { id: 'q8_d', text: 'Soft and a bit hazy', auras: ['QUIET_STORM', 'CITY_SOUL'] },
    ],
  },
  {
    id: 'q9',
    text: 'What do you actually feel like doing today?',
    hint: 'Pick up to 3.',
    type: 'multi',
    maxSelections: 3,
    options: [
      { id: 'q9_a', text: 'A city walk with no destination', auras: ['CITY_SOUL', 'NEON_WOLF'] },
      { id: 'q9_b', text: 'People-watching from a café', auras: ['CITY_SOUL', 'QUIET_STORM'] },
      { id: 'q9_c', text: 'Saying yes to something spontaneous', auras: ['SOFT_CHAOS', 'SOLAR_BURST'] },
      { id: 'q9_d', text: 'Making something with my hands', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
      { id: 'q9_e', text: 'Staying home, guilt-free', auras: ['GOLDEN_HOUR', 'QUIET_STORM'] },
    ],
  },
  {
    id: 'q10',
    text: 'What speed are you running at today?',
    hint: 'Your real speed, not your ideal one.',
    type: 'single',
    options: [
      { id: 'q10_a', text: 'Slow motion, on purpose', auras: ['GOLDEN_HOUR', 'QUIET_STORM'] },
      { id: 'q10_b', text: 'Steady, one thing at a time', auras: ['CITY_SOUL', 'SOFT_CHAOS'] },
      { id: 'q10_c', text: 'Fast and unstoppable', auras: ['SOLAR_BURST', 'NEON_WOLF'] },
      { id: 'q10_d', text: 'Sprinting on coffee alone', auras: ['SOFT_CHAOS', 'NEON_WOLF'] },
    ],
  },
  {
    id: 'q11',
    text: 'Which colour matches today?',
    hint: 'First thing that comes to mind.',
    type: 'single',
    options: [
      { id: 'q11_a', text: 'Deep violet', auras: ['QUIET_STORM', 'NEON_WOLF'] },
      { id: 'q11_b', text: 'Warm orange', auras: ['GOLDEN_HOUR', 'SOLAR_BURST'] },
      { id: 'q11_c', text: 'Electric blue', auras: ['NEON_WOLF', 'CITY_SOUL'] },
      { id: 'q11_d', text: 'Soft sage green', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
    ],
  },
  {
    id: 'q12',
    text: 'What would make today better?',
    hint: 'Pick up to 2.',
    type: 'multi',
    maxSelections: 2,
    options: [
      { id: 'q12_a', text: 'Something calm and grounding', auras: ['GOLDEN_HOUR', 'QUIET_STORM'] },
      { id: 'q12_b', text: 'Something playful and silly', auras: ['SOFT_CHAOS', 'SOLAR_BURST'] },
      { id: 'q12_c', text: 'A really deep conversation', auras: ['QUIET_STORM', 'CITY_SOUL'] },
      { id: 'q12_d', text: 'Something exciting and unexpected', auras: ['NEON_WOLF', 'SOFT_CHAOS'] },
    ],
  },
  {
    id: 'q13',
    text: 'Be honest: what’s tonight looking like?',
    hint: 'No pressure.',
    type: 'single',
    options: [
      { id: 'q13_a', text: 'Quiet, early night', auras: ['GOLDEN_HOUR', 'QUIET_STORM'] },
      { id: 'q13_b', text: 'Something low-key', auras: ['CITY_SOUL', 'GOLDEN_HOUR'] },
      { id: 'q13_c', text: 'Out, if the right plan appears', auras: ['SOFT_CHAOS', 'NEON_WOLF'] },
      { id: 'q13_d', text: 'Already booked, thanks', auras: ['SOLAR_BURST', 'NEON_WOLF'] },
    ],
  },
  {
    id: 'q14',
    text: 'A stranger can start a chat with you today if they…',
    hint: 'Set the bar.',
    type: 'single',
    options: [
      { id: 'q14_a', text: 'Ask a really interesting question', auras: ['QUIET_STORM', 'GOLDEN_HOUR'] },
      { id: 'q14_b', text: 'Make me laugh straight away', auras: ['SOFT_CHAOS', 'SOLAR_BURST'] },
      { id: 'q14_c', text: 'Compliment something oddly specific', auras: ['CITY_SOUL', 'QUIET_STORM'] },
      { id: 'q14_d', text: 'They don’t. I’m in my own world today.', auras: ['NEON_WOLF', 'QUIET_STORM'] },
    ],
  },
  {
    id: 'q15',
    text: 'Tonight gets a plot twist. Which one?',
    hint: 'The version you’d tell people about.',
    type: 'single',
    options: [
      { id: 'q15_a', text: 'I bump into someone unexpected', auras: ['SOLAR_BURST', 'SOFT_CHAOS'] },
      { id: 'q15_b', text: 'The whole plan changes last minute', auras: ['SOFT_CHAOS', 'NEON_WOLF'] },
      { id: 'q15_c', text: 'I find a place I never knew existed', auras: ['CITY_SOUL', 'NEON_WOLF'] },
      { id: 'q15_d', text: 'No twist, please. I’m happy.', auras: ['GOLDEN_HOUR', 'QUIET_STORM'] },
    ],
  },
];

export function calculateAuraScoreForQuestions(
  questions: Question[],
  answers: Record<string, string[]>,
): AuraScoreResult {
  const scoreBreakdown = createEmptyScoreBreakdown();
  let selectedCount = 0;

  for (const q of questions) {
    const selected = answers[q.id] ?? [];
    for (const opt of q.options) {
      if (selected.includes(opt.id)) {
        selectedCount += 1;
        addScores(scoreBreakdown, getOptionScores(opt));
      }
    }
  }

  if (selectedCount === 0) {
    return {
      auraType: 'CITY_SOUL',
      scoreBreakdown,
      dominantDimensions: [],
    };
  }

  const rankedDimensions = (Object.entries(scoreBreakdown) as Array<[AuraDimension, number]>)
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
  const dominantDimensions = rankedDimensions
    .filter(([, score], index) => score > 0 && (index === 0 || index === 1))
    .map(([dimension]) => dimension);
  const pairKey = dominantDimensions.slice(0, 2).sort().join(':');
  const auraType = AURA_PAIR_MAP[pairKey] ?? AURA_SINGLE_MAP[dominantDimensions[0]] ?? 'CITY_SOUL';

  return {
    auraType,
    scoreBreakdown,
    dominantDimensions,
  };
}

export function calculateAuraForQuestions(
  questions: Question[],
  answers: Record<string, string[]>,
): AuraType {
  return calculateAuraScoreForQuestions(questions, answers).auraType;
}

export function calculateAura(answers: Record<string, string[]>): AuraType {
  return calculateAuraForQuestions(DAY_VIBES_QUESTIONS, answers);
}

export function computeAlignment(
  myAnswers: Record<string, string[]>,
  theirAnswers: Record<string, string[]>,
): number {
  let totalWeight = 0;
  let sharedWeight = 0;

  for (const q of DAY_VIBES_QUESTIONS) {
    const mine = myAnswers[q.id] ?? [];
    const theirs = theirAnswers[q.id] ?? [];
    if (mine.length === 0 || theirs.length === 0) continue;
    const intersection = mine.filter(x => theirs.includes(x));
    const union = [...new Set([...mine, ...theirs])];
    const jaccard = intersection.length / union.length;
    sharedWeight += jaccard;
    totalWeight += 1;
  }

  if (totalWeight === 0) return 0;
  return Math.round((sharedWeight / totalWeight) * 100);
}

export function getAlignmentLabel(pct: number): 'instant_sync' | 'strong_sync' | 'recommended_vibe' | null {
  if (pct >= 93) return 'instant_sync';
  if (pct >= 82) return 'strong_sync';
  if (pct >= 62) return 'recommended_vibe';
  return null;
}

export function buildSharedReason(
  myAnswers: Record<string, string[]>,
  theirAnswers: Record<string, string[]>,
): string {
  const sharedTexts: string[] = [];
  for (const q of DAY_VIBES_QUESTIONS) {
    const mine = myAnswers[q.id] ?? [];
    const theirs = theirAnswers[q.id] ?? [];
    const matches = mine.filter(id => theirs.includes(id));
    for (const id of matches) {
      const opt = q.options.find(o => o.id === id);
      if (opt) sharedTexts.push(opt.text.toLowerCase());
    }
    if (sharedTexts.length >= 3) break;
  }
  if (sharedTexts.length === 0) return 'You both have aligned energy today.';
  if (sharedTexts.length === 1) return `You both chose ${sharedTexts[0]}.`;
  const last = sharedTexts.pop();
  return `You both chose ${sharedTexts.join(', ')} and ${last}.`;
}
